import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Edit,
  Delete,
  ArrowBack,
} from '@mui/icons-material';
import { Client, Match } from '@/types';
import { ClientService } from '@/services/clientService';
import { MatchingService } from '@/services/matchingService';
import ClientDetail from '@/components/ClientDetail';
import ClientInteraction from '@/components/ClientInteraction';
import ClientDocuments from '@/components/ClientDocuments';
import MatchList from '@/components/MatchList';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`client-tabpanel-${index}`}
      aria-labelledby={`client-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ClientViewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [client, setClient] = useState<Client | null>(null);
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    if (!id) {
      setError('Client ID is required');
      setLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        const [clientData, matchData] = await Promise.all([
          ClientService.getClient(id),
          MatchingService.getMatchesForClient(id)
        ]);
        
        if (clientData) {
          setClient(clientData);
          setMatches(matchData);
        } else {
          setError('Client not found');
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load client data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  const handleEdit = () => {
    if (client) {
      navigate(`/clients/${client.id}/edit`);
    }
  };

  const handleDelete = async () => {
    if (!client) return;

    setDeleting(true);
    try {
      await ClientService.deleteClient(client.id, false); // Soft delete
      navigate('/clients');
    } catch (err: any) {
      alert(`Failed to delete client: ${err.message}`);
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !client) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || 'Client not found'}</Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/clients')}
          sx={{ mt: 2 }}
        >
          Back to Clients
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/clients')}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            {client.profile.firstName} {client.profile.lastName}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Edit />}
            onClick={handleEdit}
          >
            Edit Profile
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={() => setDeleteDialogOpen(true)}
          >
            Delete
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="client details tabs">
          <Tab label="Profile Detail" />
          <Tab label={`Property Matches (${matches.length})`} />
          <Tab label={`Interactions (${client.interactions?.length || 0})`} />
          <Tab label="Legal Documents" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <ClientDetail client={client} />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <MatchList 
          matches={matches} 
          onMatchClick={(match) => navigate(`/matches/${match.id}`)}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <ClientInteraction
          clientId={client.id}
          interactions={client.interactions || []}
          onInteractionAdded={async () => {
            const updatedClient = await ClientService.getClient(client.id);
            if (updatedClient) setClient(updatedClient);
          }}
          onInteractionUpdated={async () => {
            const updatedClient = await ClientService.getClient(client.id);
            if (updatedClient) setClient(updatedClient);
          }}
          onInteractionDeleted={async () => {
            const updatedClient = await ClientService.getClient(client.id);
            if (updatedClient) setClient(updatedClient);
          }}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <ClientDocuments client={client} />
      </TabPanel>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Client</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {client.profile.firstName} {client.profile.lastName}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientViewPage;
